import { Theme, createStyles, makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { EadFields } from 'modules/archival-aid/archival-aid-children/ead-fields';
import React from 'react';
import CollectionsList from './CollectionsList';
import {
  EadTreeContextType,
  rightPanelConfig,
  useEadTreeContext,
} from './EadTreeCtx';

import AidItem from './AidItem';

import { Loader } from 'components/loader/loader';
import { ArchivalAidDetail } from 'modules/archival-aid/archival-aid-detail';
import { archivalAidConfig } from 'modules/archival-aid/archival-aid-grid';
import { ArchivalCollectionDetail } from 'modules/archival-collection/archival-collection-detail';
import { ArchivalUnitDetail } from 'modules/archival-unit-private/archival-unit-detail';
import { useParams } from 'react-router-dom';
import { GridRightPanel } from '../right-panel/grid-right-panel';
import { GridUpperPanel } from '../upper-panel/grid-upper-panel';
import AidsList from './AidsList';
import BulkExpandButtons from './BulkExpandButtons';
import { useVariantContext } from './VariantCtx';
import useProcessParams from './useProcessParams';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridRoot: {
      //height: 'calc(100vh - 80px)',
      //overflow: 'hidden',
      width: '100%',
    },
    gridCenterRoot: {
      gridGap: theme.spacing(3),
    },

    gridTitle: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 18,
      height: 60,
      lineHeight: '60px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      zIndex: 1000,
    },
    gridLeftPanel: {
      overflow: 'auto',
      position: 'sticky',
      top: 80,
      height: 'calc(100vh - 80px)',
      fontSize: 12,
      lineHeight: 1.8,
      backgroundColor: theme.palette.secondary.main,
      boxShadow: theme.shadows[1],
      fontWeight: 'normal',
    },
    upperPanel: {
      position: 'sticky',
      // because of the header height
      top: 80,
      zIndex: 5,
    },
    centralPanel: {
      height: 'calc(100vh - 80px)',
    },
    gridCentralPanelBody: {
      padding: 15,
      paddingTop: 8,
    },
    loaderOverlay: {
      position: 'fixed',
      top: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 999,
      backgroundColor: 'rgba(255,255,255,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  })
);
const NOOP_PROMISE = () => {
  return new Promise<void>(() => {});
};

const GridLeftPanel = () => {
  const { ead } = useParams<{ ead: string }>();
  const ectx = useEadTreeContext();
  const variant = useVariantContext();
  const { currentEadNode, currentArchivalAid } = ectx;
  const isEad = ead === 'ead';
  if (!currentArchivalAid && !currentEadNode) {
    return variant === 'aids' ? <AidsList /> : <CollectionsList />;
  }
  return variant === 'aids' && !isEad ? (
    <AidsList />
  ) : (
    <div style={{ padding: '0 16px' }}>
      <AidItem aid={currentArchivalAid!} level={variant === 'aids' ? 0 : 1} />
    </div>
  );
};

const GridBody = () => {
  const { level } = useParams<{ level: string }>();
  const levelInt = parseInt(level);
  const ectx = useEadTreeContext();
  const {
    currentEadNode,
    currentArchivalAid,
    currentArchivalUnit,
    currentArchivalCollection,
    setActiveEntityType,
  } = ectx;
  const variant = useVariantContext();
  const AID_LEVEL = variant === 'aids' ? 0 : 1;

  if (currentEadNode && levelInt > AID_LEVEL) {
    setActiveEntityType?.('ead');
    return <EadFields item={currentEadNode} />;
  }
  if (currentArchivalUnit) {
    setActiveEntityType?.('archival');
    return <ArchivalUnitDetail item={currentArchivalUnit} />;
  }
  if (currentArchivalAid) {
    setActiveEntityType?.('aid');
    return <ArchivalAidDetail item={currentArchivalAid} />;
  }
  if (currentArchivalCollection) {
    setActiveEntityType?.('fund');
    return <ArchivalCollectionDetail item={currentArchivalCollection} />;
  }
  setActiveEntityType?.(undefined);
  return <></>;
};

const getActiveItem = (ectx: Partial<EadTreeContextType>) => {
  switch (ectx.activeEntityType) {
    case 'fund':
      return ectx.currentArchivalCollection;
    case 'aid':
      return ectx.currentArchivalAid;
    case 'ead':
      return ectx.currentEadNode;
    case 'archival':
      return ectx.currentArchivalUnit;

    default:
      return ectx.currentArchivalCollection;
  }
};

const MainGrid = () => {
  const ectx = useEadTreeContext();
  const { localLoading } = useProcessParams();
  const { currentEadNode, crumbs, currentArchivalAid, activeEntityType } = ectx;
  const classes = useStyles();

  return (
    <Grid container className={classes.gridRoot}>
      {(ectx.loading || localLoading) && (
        <div className={classes.loaderOverlay}>
          <Loader size={80} />
        </div>
      )}
      <Grid item md={3} xs={12}>
        <div className={classes.gridLeftPanel} id="G_LEFT_PANEL">
          <BulkExpandButtons />
          <GridLeftPanel />
        </div>
      </Grid>
      <Grid item md={9} xs={12}>
        <Grid container>
          <Grid item xs={12} className={classes.upperPanel}>
            <GridUpperPanel hierarchyPath={crumbs} />
          </Grid>
          <Grid item md={9} xs={12}>
            <div className={classes.gridCentralPanelBody}>
              <GridBody />
            </div>
          </Grid>

          <Grid item md={3} xs={12}>
            <GridRightPanel
              rootArchivalAid={currentArchivalAid}
              isEad={currentEadNode !== undefined}
              item={getActiveItem(ectx)}
              refreshList={NOOP_PROMISE}
              refreshDetail={NOOP_PROMISE}
              refreshRootArchivalAid={NOOP_PROMISE}
              gridConfig={[
                activeEntityType
                  ? rightPanelConfig[activeEntityType]
                  : archivalAidConfig,
              ]}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MainGrid;
